import { combineReducers, configureStore } from '@reduxjs/toolkit'
import React from 'react'
import { Provider, type TypedUseSelectorHook, useSelector } from 'react-redux'
import { bulkUpdateSettingsApi } from '@sevenrooms/admin-lib-bulk-update-settings'
import { manageBlocklistApi } from '@sevenrooms/admin-lib-manage-blocklist'
import { timeslotViewerApi } from '@sevenrooms/admin-lib-timeslot-viewer'
import { adminAutoTagTableSliceReducer, adminAutoTagTableSliceName } from '@sevenrooms/admin-state/adminAutoTagTableSlice'
import {
  adminOngoingEmailCampaignTemplatesConfirmDeleteModalReducer,
  adminOngoingEmailCampaignTemplatesConfirmDeleteModalName,
} from '@sevenrooms/admin-state/adminOngoingEmailCampaignTemplatesConfirmDeleteModalSlice'
import {
  adminEmailCampaignTemplatesTableSliceName,
  adminEmailCampaignTemplatesTableSliceReducer,
} from '@sevenrooms/admin-state/adminOngoingEmailCampaignTemplatesTableSlice'
import { adminOngoingEmailCampaignTemplatesToggleStatusModalReducer } from '@sevenrooms/admin-state/adminOngoingEmailCampaignTemplatesToggleStatusModalSlice'
import { autoTagSlideOutSliceReducer, autoTagSlideOutSliceName } from '@sevenrooms/admin-state/autoTagSlideOutSlice'
import { customDomainApi } from '@sevenrooms/admin-state/useCustomDomainApi'
import { useVenueInteractiveFloorplanImages } from '@sevenrooms/admin-state/useVenueInteractiveFloorplanImages'
import {
  adminAutoTagsApi,
  adminClientTagsApi,
  adminEmailCampaignTemplateApi,
  adminSyncBlobsApi,
  adminVenueGroupMergeApi,
  adminVenueApi,
  adminVenueHistoryTrackingApi,
  adminVenueGroupHistoryTrackingApi,
  adminVenuePackageProvisioningApi,
  adminApiAudienceApi,
  adminApiIntegrationTypesApi,
  paymentsDebuggerApi,
  useAudienceHierarchyApi,
  eventApi,
  adminConciergeAccessReportsApi,
  venueImagesApi,
} from '@sevenrooms/core/api'
import { Root } from '@sevenrooms/core/ui-kit/layout'
import { LocaleRoot } from '@sevenrooms/locales'
import { Router } from '@sevenrooms/navigation'

export interface AdminPageProps {}

// Add reducers and middleware from RTK APIs and Slices here
const rootReducer = combineReducers({
  [adminAutoTagsApi.reducerPath]: adminAutoTagsApi.reducer,
  [adminAutoTagTableSliceName]: adminAutoTagTableSliceReducer,
  [adminSyncBlobsApi.reducerPath]: adminSyncBlobsApi.reducer,
  [autoTagSlideOutSliceName]: autoTagSlideOutSliceReducer,
  [adminEmailCampaignTemplateApi.reducerPath]: adminEmailCampaignTemplateApi.reducer,
  [adminClientTagsApi.reducerPath]: adminClientTagsApi.reducer,
  [customDomainApi.reducerPath]: customDomainApi.reducer,
  [paymentsDebuggerApi.reducerPath]: paymentsDebuggerApi.reducer,
  [adminApiAudienceApi.reducerPath]: adminApiAudienceApi.reducer,
  [adminApiIntegrationTypesApi.reducerPath]: adminApiIntegrationTypesApi.reducer,
  [adminVenueGroupMergeApi.reducerPath]: adminVenueGroupMergeApi.reducer,
  [adminVenueApi.reducerPath]: adminVenueApi.reducer,
  [adminVenueHistoryTrackingApi.reducerPath]: adminVenueHistoryTrackingApi.reducer,
  [adminVenueGroupHistoryTrackingApi.reducerPath]: adminVenueGroupHistoryTrackingApi.reducer,
  [useVenueInteractiveFloorplanImages.reducerPath]: useVenueInteractiveFloorplanImages.reducer,
  [eventApi.reducerPath]: eventApi.reducer,
  [adminConciergeAccessReportsApi.reducerPath]: adminConciergeAccessReportsApi.reducer,
  [adminVenuePackageProvisioningApi.reducerPath]: adminVenuePackageProvisioningApi.reducer,
  [adminEmailCampaignTemplatesTableSliceName]: adminEmailCampaignTemplatesTableSliceReducer,
  [adminOngoingEmailCampaignTemplatesConfirmDeleteModalName]: adminOngoingEmailCampaignTemplatesConfirmDeleteModalReducer,
  [bulkUpdateSettingsApi.reducerPath]: bulkUpdateSettingsApi.reducer,
  [manageBlocklistApi.reducerPath]: manageBlocklistApi.reducer,
  [timeslotViewerApi.reducerPath]: timeslotViewerApi.reducer,
  [useAudienceHierarchyApi.reducerPath]: useAudienceHierarchyApi.reducer,
  [venueImagesApi.reducerPath]: venueImagesApi.reducer,
  toggleAdminEmailCampaignTemplateStatusModal: adminOngoingEmailCampaignTemplatesToggleStatusModalReducer,
})

export type RootState = ReturnType<typeof rootReducer>

const store = configureStore({
  reducer: rootReducer,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware().concat([
      adminAutoTagsApi.middleware,
      adminEmailCampaignTemplateApi.middleware,
      adminSyncBlobsApi.middleware,
      adminVenueApi.middleware,
      adminVenuePackageProvisioningApi.middleware,
      useVenueInteractiveFloorplanImages.middleware,
      adminApiAudienceApi.middleware,
      adminApiIntegrationTypesApi.middleware,
      bulkUpdateSettingsApi.middleware,
      manageBlocklistApi.middleware,
      timeslotViewerApi.middleware,
      useAudienceHierarchyApi.middleware,
      eventApi.middleware,
      adminConciergeAccessReportsApi.middleware,
      venueImagesApi.middleware,
    ]),
})

export const useStoreSelector: TypedUseSelectorHook<RootState> = useSelector

export function AdminPage({ children }: React.PropsWithChildren<AdminPageProps>) {
  return (
    <Provider store={store}>
      <LocaleRoot>
        <Root theme="vx">
          <Router>{children}</Router>
        </Root>
      </LocaleRoot>
    </Provider>
  )
}
